/**
 * User identity + SPA route-change signals for Grafana Faro.
 *
 * Faro's out-of-the-box instrumentation captures hard page loads only, and
 * every session is anonymous. These two wires close the gaps that made the
 * 2026-09 usage audit blind:
 *
 *   - `useFaroUser` stamps every subsequent Faro signal (page views, web
 *     vitals, errors, traces) with the signed-in user, so "who viewed which
 *     page / who hit which error" becomes answerable. Deliberately id + role
 *     only — names and emails stay out of telemetry, matching the backend
 *     no-PII logging posture. The id is the backend `users.id`, so reports
 *     join to names in the database when a human-readable view is needed.
 *
 *   - `FaroRouteTracker` emits a `navigation.route.changed` event on every
 *     client-side route change (react-router navigations never hit the
 *     network, so nothing else records them) and sets the Faro "view" meta so
 *     subsequent signals carry the route they happened on. The route is the
 *     TEMPLATED path (`/tickets/:ticketId`, not `/tickets/1234`) so events
 *     aggregate by page and no record identifiers ride in the event body.
 *
 * Both call through `faro.api?.` — when Faro is disabled (local dev, or a
 * URL-credential route where `initFaro()` refuses to start) every call is a
 * safe no-op.
 *
 * `FaroRouteTracker` also posts the same templated route to our own
 * `pu_page_view` sink (product-usage-analytics) alongside the Faro event --
 * Faro's ~14-day retention can't answer an adoption question, so this is a
 * deliberate second, narrower sink of the same route-change signal (see
 * docs/features/product-usage-analytics/01_spec.md §2). Gated on a resolved
 * signed-in user so the landing route is posted once auth is known rather
 * than dropped (Faro's own first-page-view loses ~15% of views for exactly
 * this reason). Never awaited, never blocks render, swallows its own
 * failures -- telemetry must never break a navigation. A ref keyed on the
 * pathname + user id guards against React StrictMode's dev-only double
 * effect invocation so it never double-counts a single navigation. No
 * timestamp is sent -- the backend stamps `occurred_at` on receipt, which
 * is accurate to within milliseconds since the POST is synchronous with
 * the navigation.
 *
 * Web-shell-only by design (react-router lives here, not in shared-core).
 */

import { useEffect, useRef } from "react";
import { Outlet, useLocation, useParams } from "react-router";
import { faro } from "@grafana/faro-web-sdk";
import { useDataAdapter } from "@shared-core/data/DataAdapterContext";
import { useAuth } from "../app/auth/hooks";
import type { User } from "../app/auth/types";

export function useFaroUser(user: User | null): void {
  useEffect(() => {
    if (user) {
      faro.api?.setUser({ id: user.id, attributes: { role: user.role } });
    } else {
      faro.api?.resetUser();
    }
  }, [user]);
}

/**
 * Rebuild the route template from a concrete pathname plus react-router's
 * matched params (a layout route's `useParams()` returns the merged params of
 * the current leaf match). Each path segment that exactly equals a param
 * value becomes `:paramName`; a splat match becomes `*`.
 */
export function templatizePathname(
  pathname: string,
  params: Readonly<Record<string, string | undefined>>,
): string {
  let path = pathname || "/";

  // A splat can span multiple segments, so it can't go through the
  // segment-by-segment pass below — strip it as a suffix first.
  const splat = params["*"];
  if (splat && path.endsWith(`/${splat}`)) {
    path = `${path.slice(0, path.length - splat.length)}*`;
  }

  const valueToName = new Map<string, string>();
  for (const [name, value] of Object.entries(params)) {
    if (name !== "*" && value) valueToName.set(value, name);
  }
  if (valueToName.size === 0) return path;

  return path
    .split("/")
    .map((segment) => {
      const name = valueToName.get(segment);
      return name ? `:${name}` : segment;
    })
    .join("/");
}

/**
 * Pathless layout route wrapped around the whole route tree (see
 * `app/routes.tsx`). Renders nothing itself — it exists to observe route
 * changes from inside the router context.
 */
export function FaroRouteTracker() {
  const { pathname } = useLocation();
  const params = useParams();
  const route = templatizePathname(pathname, params);
  const { user } = useAuth();
  const dataAdapter = useDataAdapter();
  // Guards the product-usage POST against React StrictMode's dev-only
  // double effect invocation (mount -> cleanup -> mount, same instance) --
  // without this a single real navigation would post twice in dev.
  const lastPostedKeyRef = useRef<string | null>(null);

  useEffect(() => {
    faro.api?.setView({ name: route });
    faro.api?.pushEvent("navigation.route.changed", { route });
    // `route` alone under-fires (/tickets/1 -> /tickets/2 keeps the same
    // template but is a real navigation), so key on the concrete pathname.
  }, [pathname, route]);

  useEffect(() => {
    // Gate on a resolved user so the landing route is posted once auth is
    // known (re-fires when `user` transitions from null to populated).
    if (!user) return;
    const key = `${pathname}|${user.id}`;
    if (lastPostedKeyRef.current === key) return;
    lastPostedKeyRef.current = key;
    dataAdapter.recordPageView(route).catch(() => {
      // Swallow -- telemetry must never surface an error to the user or
      // affect navigation.
    });
  }, [pathname, route, user, dataAdapter]);

  return <Outlet />;
}
